import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { AiModelsCatalogModel } from '@/domain/models/ai-model.js';
import { AiCritiqueSettingsHelp, AiModelSelect, SlaFormSection } from '@/presentation/components/index.js';
import { useApp } from '@/presentation/hooks/use-app.js';

export type ClientSlaFields = {
    slaHoursEstimateEf: string;
    slaHoursStartDev: string;
    slaHoursAnalyzeBug: string;
    slaHoursAnalyzeMinor: string;
    slaDaysValidateDoc: string;
    slaMinScoreAi: string;
    aiStrictnessLevel: string;
    aiExtraSystemPrompt: string;
    aiModel: string;
};

interface Props {
    fields: ClientSlaFields;
    onFieldChange: <K extends keyof ClientSlaFields>(key: K, value: ClientSlaFields[K]) => void;
    aiModelsCatalog: AiModelsCatalogModel | null;
    aiModelsLoading: boolean;
    readOnly?: boolean;
}

export function ClientSlaSection({ fields, onFieldChange, aiModelsCatalog, aiModelsLoading, readOnly = false }: Props) {
    const { translate } = useApp();

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1 }}>
            <CardContent sx={{ p: 3, '&:last-of-type': { pb: 3 } }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {translate('clients.slaDefaults')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    {translate('clients.slaDefaultsDesc')}
                </Typography>
                <Stack spacing={2.5}>
                    <SlaFormSection title={translate('slaSettings.flowDeadlinesTitle')} description={translate('slaSettings.flowDeadlinesDesc')}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    label={`${translate('clients.slaEstimateEf')} *`}
                                    type="number"
                                    value={fields.slaHoursEstimateEf}
                                    onChange={(e) => onFieldChange('slaHoursEstimateEf', e.target.value)}
                                    size="small"
                                    fullWidth
                                    disabled={readOnly}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    label={`${translate('clients.slaStartDev')} *`}
                                    type="number"
                                    value={fields.slaHoursStartDev}
                                    onChange={(e) => onFieldChange('slaHoursStartDev', e.target.value)}
                                    size="small"
                                    fullWidth
                                    disabled={readOnly}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    label={`${translate('clients.slaAnalyzeBug')} *`}
                                    type="number"
                                    value={fields.slaHoursAnalyzeBug}
                                    onChange={(e) => onFieldChange('slaHoursAnalyzeBug', e.target.value)}
                                    size="small"
                                    fullWidth
                                    disabled={readOnly}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    label={`${translate('clients.slaAnalyzeMinor')} *`}
                                    type="number"
                                    value={fields.slaHoursAnalyzeMinor}
                                    onChange={(e) => onFieldChange('slaHoursAnalyzeMinor', e.target.value)}
                                    size="small"
                                    fullWidth
                                    disabled={readOnly}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    label={`${translate('clients.slaValidateDoc')} *`}
                                    type="number"
                                    value={fields.slaDaysValidateDoc}
                                    onChange={(e) => onFieldChange('slaDaysValidateDoc', e.target.value)}
                                    size="small"
                                    fullWidth
                                    disabled={readOnly}
                                />
                            </Grid>
                        </Grid>
                    </SlaFormSection>
                    <SlaFormSection title={translate('slaSettings.aiCritiqueTitle')} description={translate('slaSettings.aiCritiqueDesc')}>
                        <Stack spacing={2}>
                            <AiCritiqueSettingsHelp />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <TextField
                                        label={`${translate('aiCritique.strictnessLabel')} *`}
                                        type="number"
                                        value={fields.aiStrictnessLevel}
                                        onChange={(e) => onFieldChange('aiStrictnessLevel', e.target.value)}
                                        size="small"
                                        fullWidth
                                        disabled={readOnly}
                                        inputProps={{ min: 1, max: 10, step: 1 }}
                                        helperText={translate('aiCritique.strictnessHelp')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <TextField
                                        label={`${translate('aiCritique.minScoreLabel')} *`}
                                        type="number"
                                        value={fields.slaMinScoreAi}
                                        onChange={(e) => onFieldChange('slaMinScoreAi', e.target.value)}
                                        size="small"
                                        fullWidth
                                        disabled={readOnly}
                                        inputProps={{ step: '0.01', min: 0, max: 10 }}
                                        helperText={translate('aiCritique.minScoreHelp')}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <AiModelSelect
                                        label={translate('clients.aiModelLabel')}
                                        value={fields.aiModel}
                                        onChange={(value) => onFieldChange('aiModel', value)}
                                        catalog={aiModelsCatalog}
                                        isLoading={aiModelsLoading}
                                        disabled={readOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label={translate('clients.aiExtraSystemPrompt')}
                                        value={fields.aiExtraSystemPrompt}
                                        onChange={(e) => onFieldChange('aiExtraSystemPrompt', e.target.value)}
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        disabled={readOnly}
                                        helperText={translate('clients.aiExtraSystemPromptHelp')}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </SlaFormSection>
                </Stack>
            </CardContent>
        </Card>
    );
}
